import React, { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Publication,
  createPublication,
  updatePublication,
  deletePublication,
  updatePublicationOrder,
} from "../firebase/publicationService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";
import { publicationsQuery } from "../queries/contentQueries";

interface PublicationContextProps {
  publications: Publication[];
  loading: boolean;
  error: string | null;
  refreshPublications: () => Promise<void>;
  addPublication: (
    publication: Omit<Publication, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editPublication: (
    id: string,
    publication: Partial<Omit<Publication, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removePublication: (id: string) => Promise<void>;
  reorderPublications: (
    reorderedPublications: { id: string; order: number }[]
  ) => Promise<void>;
}

const EMPTY: Publication[] = [];

export const usePublications = (): PublicationContextProps => {
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery(publicationsQuery);

  const setPublications = (update: (prev: Publication[]) => Publication[]) =>
    queryClient.setQueryData<Publication[]>(
      publicationsQuery.queryKey,
      (prev) => update(prev ?? [])
    );

  const refreshPublications = async () => {
    await refetch();
  };

  const addPublication = async (
    publication: Omit<Publication, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-add")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    const id = await withFirebaseRetry(async () =>
      createPublication(publication)
    );

    setPublications((prev) => [
      ...prev,
      { ...publication, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() },
    ]);

    return id;
  };

  const editPublication = async (
    id: string,
    publication: Partial<Omit<Publication, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-edit")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => updatePublication(id, publication));

    setPublications((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, ...publication, updatedAt: Timestamp.now() }
          : item
      )
    );
  };

  const removePublication = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-remove")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => deletePublication(id));
    setPublications((prev) => prev.filter((p) => p.id !== id));
  };

  const reorderPublications = async (
    reorderedPublications: { id: string; order: number }[]
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-reorder")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () =>
      updatePublicationOrder(reorderedPublications)
    );

    // For reorder operations, we need to refresh to get the correct order
    await refreshPublications();
  };

  return {
    publications: data ?? EMPTY,
    loading: isPending,
    error: error ? "Failed to load publications. Please try again later." : null,
    refreshPublications,
    addPublication,
    editPublication,
    removePublication,
    reorderPublications,
  };
};

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const PublicationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => <>{children}</>;
